import React, { useState, useEffect } from 'react';
import { Settings, Save, RotateCcw, AlertCircle, CheckCircle, Plus, Trash2, FileText, Bot, Megaphone } from 'lucide-react';

const SettingsPage = () => {
  const [settings, setSettings] = useState({
    openaiOutlinePrompt: `You are a professional video script writer specializing in creating engaging, educational content for YouTube and social media platforms.

Your task is to transform the provided article content into a compelling video script that:
- Captures the main points clearly and concisely
- Uses engaging, conversational language
- Includes natural transitions and flow
- Is optimized for video format (visual, dynamic)
- Maintains the original message while making it more accessible
- Uses storytelling techniques to keep viewers engaged

Guidelines:
- Keep sentences short and punchy for better video pacing
- Use "you" to directly address the viewer
- Include calls-to-action where appropriate
- Make it feel like a conversation, not a lecture
- Aim for 2-3 minutes of speaking time (approximately 300-450 words)
- Structure with a clear beginning, middle, and end

Transform the following article into a video script:`,
    openaiScriptPrompt: `You are a professional video script writer specializing in creating engaging, educational content for YouTube and social media platforms.

Your task is to transform the provided article content into a compelling video script that:
- Captures the main points clearly and concisely
- Uses engaging, conversational language
- Includes natural transitions and flow
- Is optimized for video format (visual, dynamic)
- Maintains the original message while making it more accessible
- Uses storytelling techniques to keep viewers engaged

Guidelines:
- Keep sentences short and punchy for better video pacing
- Use "you" to directly address the viewer
- Include calls-to-action where appropriate
- Make it feel like a conversation, not a lecture
- Aim for 2-3 minutes of speaking time (approximately 300-450 words)
- Structure with a clear beginning, middle, and end

Transform the following article into a video script:`,
    ctas: [
      "Subscribe to our channel for more content like this!",
      "Hit the like button if this helped you!",
      "Follow us on social media for daily tips!"
    ]
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [activeSection, setActiveSection] = useState('outline');

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch('/api/settings');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.settings) {
          // Convert backend format to frontend format
          const frontendSettings = {
            openaiOutlinePrompt: data.settings.openai_outline_prompt || settings.openaiOutlinePrompt,
            openaiScriptPrompt: data.settings.openai_script_prompt || settings.openaiScriptPrompt,
            ctas: data.settings.ctas ? JSON.parse(data.settings.ctas) : settings.ctas
          };
          setSettings(prev => ({ ...prev, ...frontendSettings }));
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      const response = await fetch('/api/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        setMessage('Settings saved successfully!');
        setMessageType('success');
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Failed to save settings. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      openaiOutlinePrompt: `You are a professional video script writer specializing in creating engaging, educational content for YouTube and social media platforms.

Your task is to transform the provided article content into a compelling video script that:
- Captures the main points clearly and concisely
- Uses engaging, conversational language
- Includes natural transitions and flow
- Is optimized for video format (visual, dynamic)
- Maintains the original message while making it more accessible
- Uses storytelling techniques to keep viewers engaged

Guidelines:
- Keep sentences short and punchy for better video pacing
- Use "you" to directly address the viewer
- Include calls-to-action where appropriate
- Make it feel like a conversation, not a lecture
- Aim for 2-3 minutes of speaking time (approximately 300-450 words)
- Structure with a clear beginning, middle, and end

Transform the following article into a video script:`,
      openaiScriptPrompt: `You are a professional video script writer specializing in creating engaging, educational content for YouTube and social media platforms.

Your task is to transform the provided article content into a compelling video script that:
- Captures the main points clearly and concisely
- Uses engaging, conversational language
- Includes natural transitions and flow
- Is optimized for video format (visual, dynamic)
- Maintains the original message while making it more accessible
- Uses storytelling techniques to keep viewers engaged

Guidelines:
- Keep sentences short and punchy for better video pacing
- Use "you" to directly address the viewer
- Include calls-to-action where appropriate
- Make it feel like a conversation, not a lecture
- Aim for 2-3 minutes of speaking time (approximately 300-450 words)
- Structure with a clear beginning, middle, and end

Transform the following article into a video script:`,
      ctas: [
        "Subscribe to our channel for more content like this!",
        "Hit the like button if this helped you!",
        "Follow us on social media for daily tips!"
      ]
    });
    setMessage('Settings reset to defaults');
    setMessageType('info');
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  const addCTA = () => {
    setSettings(prev => ({
      ...prev,
      ctas: [...prev.ctas, "New call-to-action"]
    }));
  };

  const removeCTA = (index) => {
    setSettings(prev => ({
      ...prev,
      ctas: prev.ctas.filter((_, i) => i !== index)
    }));
  };

  const updateCTA = (index, value) => {
    setSettings(prev => ({
      ...prev,
      ctas: prev.ctas.map((cta, i) => i === index ? value : cta)
    }));
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'outline':
        return (
          <div className="space-y-4">
            <div>
              <label htmlFor="openaiOutlinePrompt" className="block text-sm font-medium text-gray-700 mb-2">
                Outline Generation Prompt
              </label>
              <textarea
                id="openaiOutlinePrompt"
                value={settings.openaiOutlinePrompt}
                onChange={(e) => setSettings(prev => ({ ...prev, openaiOutlinePrompt: e.target.value }))}
                rows={12}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors font-mono text-sm"
                placeholder="Enter your custom OpenAI prompt for outline generation..."
              />
              <p className="mt-2 text-sm text-gray-500">
                This prompt will be sent to OpenAI along with your article content to generate the video outline.
                The AI will use this prompt to understand how to structure your content.
              </p>
            </div>
          </div>
        );
      
      case 'script':
        return (
          <div className="space-y-4">
            <div>
              <label htmlFor="openaiScriptPrompt" className="block text-sm font-medium text-gray-700 mb-2">
                Script Generation Prompt
              </label>
              <textarea
                id="openaiScriptPrompt"
                value={settings.openaiScriptPrompt}
                onChange={(e) => setSettings(prev => ({ ...prev, openaiScriptPrompt: e.target.value }))}
                rows={12}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors font-mono text-sm"
                placeholder="Enter your custom OpenAI prompt for script generation..."
              />
              <p className="mt-2 text-sm text-gray-500">
                This prompt will be sent to OpenAI along with your article content to generate the video script.
                The AI will use this prompt to understand how to transform your content.
              </p>
            </div>
          </div>
        );
      
      case 'ctas':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Call-to-Action Templates</h3>
              <button
                onClick={addCTA}
                className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add New</span>
              </button>
            </div>
            <p className="text-sm text-gray-600">
              These CTAs will be available to append to the end of your video scripts. You can add as many as you need.
            </p>
            <div className="space-y-3">
              {settings.ctas.map((cta, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                  <input
                    type="text"
                    value={cta}
                    onChange={(e) => updateCTA(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    placeholder="Enter call-to-action text..."
                  />
                  <button
                    onClick={() => removeCTA(index)}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove CTA"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <Settings className="h-8 w-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        </div>
        <p className="text-gray-600">
          Configure your YPN Video Creator preferences and AI prompts
        </p>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
          messageType === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          messageType === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          {messageType === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : messageType === 'error' ? (
            <AlertCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{message}</span>
        </div>
      )}

      {/* Main Layout */}
      <div className="flex gap-6">
        {/* Left Sidebar */}
        <div className="w-64 flex-shrink-0">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Settings Sections</h2>
            <nav className="space-y-2">
              <button
                onClick={() => setActiveSection('outline')}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  activeSection === 'outline'
                    ? 'bg-purple-100 text-purple-700 border border-purple-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <FileText className="h-5 w-5" />
                <span>Outline AI Prompt</span>
              </button>
              <button
                onClick={() => setActiveSection('script')}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  activeSection === 'script'
                    ? 'bg-purple-100 text-purple-700 border border-purple-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Bot className="h-5 w-5" />
                <span>Script AI Prompt</span>
              </button>
              <button
                onClick={() => setActiveSection('ctas')}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  activeSection === 'ctas'
                    ? 'bg-purple-100 text-purple-700 border border-purple-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Megaphone className="h-5 w-5" />
                <span>CTAs</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Section Header */}
            <div className="mb-6">
              {activeSection === 'outline' && (
                <>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    📝 AI Outline Generation
                  </h2>
                  <p className="text-gray-600">
                    Customize the prompt used by OpenAI to generate video outlines from your articles.
                    This prompt defines how the AI creates structured outlines for your content.
                  </p>
                </>
              )}
              {activeSection === 'script' && (
                <>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    🤖 AI Script Generation
                  </h2>
                  <p className="text-gray-600">
                    Customize the prompt used by OpenAI to generate video scripts from your articles.
                    This prompt defines how the AI interprets and transforms your content.
                  </p>
                </>
              )}
              {activeSection === 'ctas' && (
                <>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    📢 Call-to-Action Templates
                  </h2>
                  <p className="text-gray-600">
                    Manage your call-to-action templates that can be appended to video scripts.
                    These help increase engagement and drive viewer actions.
                  </p>
                </>
              )}
            </div>

            {/* Dynamic Content */}
            {renderContent()}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex items-center justify-between bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={resetToDefaults}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
                <span>Reset to Defaults</span>
              </button>
            </div>
            
            <button
              onClick={saveSettings}
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save Settings</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          💡 Tips for Custom Prompts
        </h3>
        <ul className="space-y-2 text-blue-800">
          <li className="flex items-start space-x-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Be specific about the tone and style you want (professional, casual, educational, etc.)</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Include target audience information (beginners, experts, general public)</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Specify desired length and format requirements</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Add any specific instructions for calls-to-action or engagement</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SettingsPage;
